import { useMemo, useState } from "react";
import { Typography, Box, Button } from "@mui/material";
import PartnerShoppingList from "./PartnerShoppingList";
import { useGlobalAuthContext } from "../context/AuthContext";
import { getSpatialChipSx } from "../theme/spatial";

const ItemsOnlyList = ({ items, index, furniture }) => {
  const lItemsArray = useMemo(() => {
    if (!items?.[index]?.furniture) return [];

    const cleanedFurnitureId = furniture?.substring(furniture?.indexOf("-") + 1);
    const result = [];

    const specificFurniture = cleanedFurnitureId
      ? items[index].furniture.find((f) => f._id === cleanedFurnitureId)
      : null;

    const furnitureToProcess = specificFurniture
      ? [specificFurniture]
      : items[index].furniture;

    furnitureToProcess.forEach((item) => {
      const lItemsToProcess =
        item?.lItems?.length > 0 ? item.lItems : [item];

      lItemsToProcess.forEach((lItem) => {
        const baseId = lItem?._id;
        const capacity =
          lItem.capacity && !isNaN(lItem.capacity) ? +lItem.capacity : 1;
        const existing = result.findIndex((e) => e._id === baseId);

        if (existing !== -1) {
          result[existing].capacity += capacity;
        } else {
          result.push({
            _id: baseId,
            name: lItem?.name || "No Name",
            capacity,
          });
        }
      });
    });

    return result.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true })
    );
  }, [items, index, furniture]);

  if (lItemsArray.length === 0) {
    return (
      <Typography
        sx={{ py: 3, color: "rgba(60, 60, 67, 0.45)", textAlign: "center", fontSize: "13px" }}
      >
        No items to display
      </Typography>
    );
  }

  return (
    <Box>
      {lItemsArray.map((item) => (
        <Box
          key={item._id}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1,
            borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
          }}
        >
          <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "rgba(60, 60, 67, 0.85)" }}>
            {item.name}
          </Typography>
          <Typography sx={{ fontSize: "11px", color: "rgba(60, 60, 67, 0.45)", flexShrink: 0, ml: 1 }}>
            Qty: {item.capacity || 1}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const ShoppingContent = ({
  roomActive,
  roomsData,
  setSelectedTab,
  selectedTab,
  roomActiveLayout,
  getRoomLabel,
  containerRef,
  isMobile,
  furniture,
}) => {
  const [cartItems, setCartItems] = useState([]);
  const {partnerPackage} = useGlobalAuthContext();
  const [viewAll, setViewAll] = useState(true);
  
  const finalLayout = useMemo(() => {
    const extractedLayouts = roomActiveLayout?.map((index, i) => {
      const room = roomsData?.[i];
      return room?.layouts?.[index] || null;
    });
  
    const allLItems = extractedLayouts?.reduce((acc, layout) => {
      if (!layout?.furniture?.length) return acc;
  
      layout.furniture.forEach(furnitureItem => {
        const lItems = furnitureItem?.lItems;
        if (Array.isArray(lItems) && lItems.length > 0) {
          acc.push(...lItems);
        } else {
          acc.push(furnitureItem);
        }
      });
  
      return acc;
    }, []);
  
    return [{
      furniture: [{ lItems: allLItems }]
    }];
  }, [roomActiveLayout, roomsData]); 

  return (
    <>
      <Box
        sx={{
          py: 1,
          display: "flex",
          alignItems: "center",
          gap: "5px",
          overflowX: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {/* View All chip — always first, always visible */}
        <Button
          key="view-all-chip"
          onClick={() => {
            setViewAll(true);
            setSelectedTab(-1);
          }}
          sx={getSpatialChipSx({ selected: viewAll })}
        >
          View All
        </Button>

        {/* "Selected" chip when furniture is selected */}
        {furniture && (
          <Button
            key="selected-chip"
            onClick={() => {
              setSelectedTab(0);
              setViewAll(false);
            }}
            sx={getSpatialChipSx({ selected: !viewAll && selectedTab === 0 })}
          >
            Selected
          </Button>
        )}

        {/* Room zone chips */}
        {roomsData?.map((room, idx) => {
          const chipIndex = furniture ? idx + 1 : idx;
          const hasFurniture =
            room.layouts?.[roomActiveLayout?.[idx]]?.furniture?.length > 0;

          if (!hasFurniture) return null;

          const isActive = !viewAll && selectedTab === chipIndex;

          return (
            <Button
              key={room.roomId || idx}
              onClick={() => {
                setSelectedTab(chipIndex);
                setViewAll(false);
              }}
              sx={getSpatialChipSx({ selected: isActive })}
            >
              {getRoomLabel(room?.zones || "")}
            </Button>
          );
        })}
      </Box>

      {partnerPackage?.productVisibility ? (
        <PartnerShoppingList
          cartItems={cartItems}
          setCartItems={setCartItems}
          key={viewAll ? "viewAll" : selectedTab}
          items={
            viewAll ? finalLayout : roomActive === -1 || !furniture
              ? roomsData[selectedTab]?.layouts
              : furniture && selectedTab === 0
              ? roomsData[roomActive]?.layouts
              : roomsData[selectedTab - 1]?.layouts
          }
          index={
            viewAll ? 0 : roomActive === -1 || !furniture
              ? roomActiveLayout?.[selectedTab] || 0
              : selectedTab === 0
              ? roomActiveLayout?.[roomActive]
              : roomActiveLayout?.[selectedTab - 1] || 0
          }
          furniture={furniture && selectedTab === 0 ? furniture : ""}
          containerRef={containerRef}
          isMobile={isMobile}
        />
      ) : (
        <ItemsOnlyList
          key={viewAll ? "viewAll" : selectedTab}
          items={
            viewAll ? finalLayout : roomActive === -1 || !furniture
              ? roomsData[selectedTab]?.layouts
              : furniture && selectedTab === 0
              ? roomsData[roomActive]?.layouts
              : roomsData[selectedTab - 1]?.layouts
          }
          index={
            viewAll ? 0 : roomActive === -1 || !furniture
              ? roomActiveLayout?.[selectedTab] || 0
              : selectedTab === 0
              ? roomActiveLayout?.[roomActive]
              : roomActiveLayout?.[selectedTab - 1] || 0
          }
          furniture={furniture && selectedTab === 0 ? furniture : ""}
        />
      )}
    </>
  );
};

export default ShoppingContent;
